// Google Analytics 工具类
import { GA_CONFIG, GA_EVENTS, GA_DIMENSIONS } from '../config/analytics'

// 事件参数接口
interface EventParameters {
  [key: string]: any
}

// 页面参数接口
interface PageViewParameters {
  page_title?: string
  page_location?: string
  page_path?: string
  custom_map?: { [key: string]: string }
}

class AnalyticsManager {
  private isInitialized = false
  private gtag: any = null

  // 初始化Google Analytics
  public async initialize(): Promise<void> {
    if (this.isInitialized) return

    try {
      // 检查是否有测量ID
      if (!GA_CONFIG.MEASUREMENT_ID || GA_CONFIG.MEASUREMENT_ID === 'G-XXXXXXXXXX') {
        console.warn('Google Analytics: 请配置正确的测量ID')
        return
      }

      // 获取全局gtag函数
      this.gtag = (window as any).gtag

      if (!this.gtag) {
        console.error('Google Analytics: gtag函数未找到，请确保vue-gtag已正确初始化')
        return
      }

      this.isInitialized = true
      console.log('Google Analytics 初始化成功')
    } catch (error) {
      console.error('Google Analytics 初始化失败:', error)
    }
  }

  // 发送页面浏览事件
  public trackPageView(pagePath?: string, pageTitle?: string): void {
    if (!this.isInitialized) return

    const params: PageViewParameters = {
      page_title: pageTitle || document.title,
      page_location: window.location.href,
      page_path: pagePath || window.location.pathname,
      custom_map: GA_CONFIG.CUSTOM_CONFIG.custom_map
    }

    this.gtag?.('event', GA_EVENTS.PAGE_VIEW, params)
  }

  // 发送自定义事件
  public trackEvent(eventName: string, parameters?: EventParameters): void {
    if (!this.isInitialized) return

    this.gtag?.('event', eventName, parameters)
  }

  // 发送用户反馈事件
  public trackFeedbackOpen(): void {
    this.trackEvent(GA_EVENTS.FEEDBACK_OPEN, {
      event_category: 'user_feedback',
      event_label: 'feedback_form_opened'
    })
  }

  public trackFeedbackSubmit(rating: number, messageLength: number): void {
    this.trackEvent(GA_EVENTS.FEEDBACK_SUBMIT, {
      event_category: 'user_feedback',
      event_label: 'feedback_submitted',
      [GA_DIMENSIONS.USER_RATING]: rating,
      message_length: messageLength
    })
  }

  public trackFeedbackSuccess(rating: number): void {
    this.trackEvent(GA_EVENTS.FEEDBACK_SUCCESS, {
      event_category: 'user_feedback',
      event_label: 'feedback_success',
      [GA_DIMENSIONS.USER_RATING]: rating
    })
  }

  public trackFeedbackError(error: string): void {
    this.trackEvent(GA_EVENTS.FEEDBACK_ERROR, {
      event_category: 'user_feedback',
      event_label: 'feedback_error',
      error_message: error
    })
  }

  // 发送图片处理事件
  public trackImageUpload(fileType: string, fileSize: number): void {
    this.trackEvent(GA_EVENTS.IMAGE_UPLOAD, {
      event_category: 'image_processing',
      event_label: 'image_uploaded',
      [GA_DIMENSIONS.IMAGE_TYPE]: fileType,
      file_size: fileSize
    })
  }

  public trackImageExtractColors(gridWidth: number, gridHeight: number, colorCount: number): void {
    this.trackEvent(GA_EVENTS.IMAGE_EXTRACT_COLORS, {
      event_category: 'image_processing',
      event_label: 'colors_extracted',
      [GA_DIMENSIONS.GRID_SIZE]: `${gridWidth}x${gridHeight}`,
      [GA_DIMENSIONS.COLOR_COUNT]: colorCount
    })
  }

  public trackImageDownload(downloadType: string): void {
    this.trackEvent(GA_EVENTS.IMAGE_DOWNLOAD, {
      event_category: 'image_processing',
      event_label: 'image_downloaded',
      download_type: downloadType
    })
  }

  // 发送颜色管理事件
  public trackColorAdd(colorCount: number): void {
    this.trackEvent(GA_EVENTS.COLOR_ADD, {
      event_category: 'color_management',
      event_label: 'color_added',
      [GA_DIMENSIONS.COLOR_COUNT]: colorCount
    })
  }

  public trackColorDelete(colorCount: number): void {
    this.trackEvent(GA_EVENTS.COLOR_DELETE, {
      event_category: 'color_management',
      event_label: 'color_deleted',
      [GA_DIMENSIONS.COLOR_COUNT]: colorCount
    })
  }

  public trackColorExport(colorCount: number): void {
    this.trackEvent(GA_EVENTS.COLOR_EXPORT, {
      event_category: 'color_management',
      event_label: 'colors_exported',
      [GA_DIMENSIONS.COLOR_COUNT]: colorCount
    })
  }

  public trackColorImport(colorCount: number): void {
    this.trackEvent(GA_EVENTS.COLOR_IMPORT, {
      event_category: 'color_management',
      event_label: 'colors_imported',
      [GA_DIMENSIONS.COLOR_COUNT]: colorCount
    })
  }

  // 发送语言切换事件
  public trackLanguageSwitch(fromLang: string, toLang: string): void {
    this.trackEvent(GA_EVENTS.LANGUAGE_SWITCH, {
      event_category: 'user_preference',
      event_label: 'language_changed',
      from_language: fromLang,
      to_language: toLang,
      [GA_DIMENSIONS.USER_LANGUAGE]: toLang
    })
  }

  // 发送错误事件
  public trackError(errorType: string, errorMessage: string, errorStack?: string): void {
    this.trackEvent(GA_EVENTS.ERROR_OCCURRED, {
      event_category: 'error',
      event_label: errorType,
      error_message: errorMessage,
      error_stack: errorStack
    })
  }

  // 设置用户属性
  public setUserProperty(property: string, value: any): void {
    if (!this.isInitialized) return

    this.gtag?.('config', GA_CONFIG.MEASUREMENT_ID, {
      [property]: value
    })
  }

  // 设置用户ID
  public setUserId(userId: string): void {
    if (!this.isInitialized) return

    this.gtag?.('config', GA_CONFIG.MEASUREMENT_ID, {
      user_id: userId
    })
  }

  // 检查是否已初始化
  public get initialized(): boolean {
    return this.isInitialized
  }
}

// 创建单例实例
export const analytics = new AnalyticsManager()

// 全局错误处理
window.addEventListener('error', (event) => {
  analytics.trackError('javascript_error', event.message, event.filename)
})

window.addEventListener('unhandledrejection', (event) => {
  analytics.trackError('promise_rejection', event.reason?.toString() || 'Unknown promise rejection')
})
